/**
 * micro:bit 라디오 광장(PICNIC-MB-FIELD-001 v0.2 §2 통로 ③ · §6 2단계) — 패킷 읽기 · 광장 기록(순수).
 *
 * 길: 내 시뮬레이터 → Bench 편집기 탭(`bench.mbRadio` · 그룹 · 패킷 바이트) → 이 탭 → 룸 서버 `mbradio`(코스마다 하나) → 친구 탭
 *     → 친구의 Bench 탭(`field.mbRadio`) → 친구 시뮬레이터(radiopacket). 그룹은 받는 시뮬레이터가 거른다 — 여기서는 화면에 '같은 그룹'만 센다.
 * 패킷 바이트는 MakeCode 라디오 그대로(pxt-common-packages libs/radio): [0]=종류 · [1..4]=시각 · [5..8]=일련번호 · [9..]=값.
 *   종류 0 숫자(Int32LE) · 1 이름-값(Int32LE + 길이 + 이름) · 2 글자(길이 + UTF-8) · 4 실수(Float64LE) · 5 이름-실수(Float64LE + 길이 + 이름).
 */

export const MB_RADIO_OUT = "bench.mbRadio";
export const MB_RADIO_IN = "field.mbRadio";
const PREFIX = 9;

export type RadioValue =
  | { readonly kind: "number"; readonly n: number }
  | { readonly kind: "string"; readonly s: string }
  | { readonly kind: "value"; readonly name: string; readonly n: number }
  | { readonly kind: "other" };

const view = (p: readonly number[]) => new DataView(Uint8Array.from(p).buffer);
const str = (p: readonly number[], at: number): string => {
  const len = p[at] ?? 0;
  const bytes = Uint8Array.from(p.slice(at + 1, at + 1 + len));
  try { return new TextDecoder().decode(bytes); } catch { return ""; }
};
const round = (n: number) => Math.round(n * 1000) / 1000;

/** 패킷 바이트 → 값(모르는 종류 · 짧은 패킷은 other) */
export function decodeRadio(p: readonly number[]): RadioValue {
  const t = p[0];
  try {
    if (t === 0 && p.length >= PREFIX + 4) return { kind: "number", n: view(p).getInt32(PREFIX, true) };
    if (t === 1 && p.length >= PREFIX + 5) return { kind: "value", n: view(p).getInt32(PREFIX, true), name: str(p, PREFIX + 4) };
    if (t === 2 && p.length >= PREFIX + 1) return { kind: "string", s: str(p, PREFIX) };
    if (t === 4 && p.length >= PREFIX + 8) return { kind: "number", n: round(view(p).getFloat64(PREFIX, true)) };
    if (t === 5 && p.length >= PREFIX + 9) return { kind: "value", n: round(view(p).getFloat64(PREFIX, true)), name: str(p, PREFIX + 8) };
  } catch { /* 짧은 패킷 */ }
  return { kind: "other" };
}

/** Bench 탭이 보낸 내 시뮬레이터의 패킷 — 출처 · 종류 · 판 · 모양이 맞을 때만 */
export function acceptBenchRadio(ev: Pick<MessageEvent, "origin" | "data">, benchOrigin: string | null): { g: number; p: number[] } | null {
  if (!benchOrigin || ev.origin !== benchOrigin) return null;
  const d = ev.data as { type?: unknown; v?: unknown; g?: unknown; p?: unknown } | null;
  if (!d || d.type !== MB_RADIO_OUT || d.v !== 1) return null;
  return packetOf(d.g, d.p);
}

/** 그룹 · 바이트 검사(0..255 · 1..64 바이트 — 시뮬레이터 패킷은 36바이트였다 · 룸 MBRADIO_LIMITS 와 같게) */
export function packetOf(g: unknown, p: unknown): { g: number; p: number[] } | null {
  if (!Number.isInteger(g) || (g as number) < 0 || (g as number) > 255) return null;
  if (!Array.isArray(p) || p.length < 1 || p.length > 64) return null;
  if (!p.every((b) => Number.isInteger(b) && b >= 0 && b <= 255)) return null;
  return { g: g as number, p: p as number[] };
}

// ── 광장 기록 ────────────────────────────────────────────────────────

export interface RadioEntry {
  readonly dir: "out" | "in";
  /** 보낸 친구 별명(in) · 나(out)는 null */
  readonly name: string | null;
  readonly g: number;
  readonly value: RadioValue;
  /** 진짜 보드 게이트웨이가 교실 공중에서 들은 패킷(§6.7) */
  readonly air?: boolean;
}

export interface Plaza {
  /** 최근 기록(12개) */
  readonly log: readonly RadioEntry[];
  /** 내 그룹 — 내 시뮬레이터가 마지막으로 보낸 패킷의 그룹(아직 안 보냈으면 null) */
  readonly group: number | null;
  readonly sent: number;
  /** 받은 것(내 그룹 — 그룹을 모르면 모두) */
  readonly received: number;
  /** 같은 그룹에서 받은 친구 별명 */
  readonly partners: readonly string[];
  /** 오간 것 모두 센 수(보냄 + 받음 · 3D 광장이 새 전파를 안다) */
  readonly n?: number;
}

export const initialPlaza = (): Plaza => ({ log: [], group: null, sent: 0, received: 0, partners: [] });
export const EXCHANGE_GOAL = 3;

export function plazaOut(s: Plaza, g: number, p: readonly number[]): Plaza {
  return { ...s, group: g, sent: s.sent + 1, n: (s.n ?? 0) + 1, log: [...s.log, { dir: "out" as const, name: null, g, value: decodeRadio(p) }].slice(-12) };
}

export function plazaIn(s: Plaza, name: string, g: number, p: readonly number[], air = false): Plaza {
  const mine = s.group === null || s.group === g;
  return {
    ...s,
    received: s.received + (mine ? 1 : 0),
    n: (s.n ?? 0) + 1,
    partners: mine && name && !s.partners.includes(name) ? [...s.partners, name].slice(-30) : s.partners,
    log: [...s.log, { dir: "in" as const, name, g, value: decodeRadio(p), ...(air ? { air: true } : {}) }].slice(-12),
  };
}

/** 주고받기 — 내가 보내고 같은 그룹 친구에게서 받은 것이 각각 3번 */
export const exchanged = (s: Plaza): boolean => s.sent >= EXCHANGE_GOAL && s.received >= EXCHANGE_GOAL && s.group !== null;

/** 짝 화면(AI 짝에게 놀이) — 내가 보낸 수 · 같은 그룹 짝 이름 · 짝에게서 온 것(최근 n개 · 새것이 먼저 · 그룹을 모르면 모두) */
export function partnerView(s: Plaza, n = 3): { me: number; names: readonly string[]; got: { name: string; value: RadioValue }[] } {
  const got = s.log.filter((e) => e.dir === "in" && (s.group === null || e.g === s.group)).slice(-n).reverse().map((e) => ({ name: e.name ?? "?", value: e.value }));
  return { me: s.sent, names: s.partners, got };
}
